import React, { useEffect, useState } from 'react';

interface ThinkingIndicatorProps {
  currentStage?: string;
  stageMessage?: string;
  progress?: number; // Add progress support (0-100)
  hideText?: boolean; // New prop to hide text when MessageThinking is shown
}

type StageKey = 'pending' | 'understanding' | 'knowledge' | 'sql' | 'visualization' | 'response' | 'citations';

const STAGE_MAP: Record<StageKey, { label: string; index: number }> = {
  'pending': { label: 'Starting...', index: 0 },
  'understanding': { label: 'Understanding question', index: 1 },
  'knowledge': { label: 'Gathering relevant information', index: 2 },
  'sql': { label: 'Analyzing data', index: 3 },
  'visualization': { label: 'Creating visualizations', index: 4 },
  'response': { label: 'Preparing response', index: 5 },
  'citations': { label: 'Adding citations...', index: 6 },
};

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({ 
  currentStage, 
  stageMessage,
  progress,
  hideText = false
}) => {
  const [stageIdx, setStageIdx] = useState(0);
  const [displayMessage, setDisplayMessage] = useState('Starting...');
  const [fallbackActive, setFallbackActive] = useState(false);

  useEffect(() => {
    if (currentStage && currentStage in STAGE_MAP) {
      const stage = STAGE_MAP[currentStage as StageKey];
      setStageIdx(stage.index);
      setDisplayMessage(stageMessage || stage.label);
      setFallbackActive(false); // Reset fallback when we get real updates
    } else if (currentStage) {
      // Fallback for unknown stages
      setDisplayMessage(stageMessage || `Processing (${currentStage})...`);
      setFallbackActive(false);
    }
  }, [currentStage, stageMessage]);

  // Fallback mechanism: if we don't get any stage updates within 15 seconds, show progress anyway
  useEffect(() => {
    if (!currentStage || currentStage === 'pending') {
      const fallbackTimer = setTimeout(() => {
        if (!currentStage || currentStage === 'pending') {
          console.warn('[THINKING_INDICATOR] No progress updates received, activating fallback progression');
          setFallbackActive(true);
          setStageIdx(1);
          setDisplayMessage('Processing your request...');
        }
      }, 15000);

      return () => clearTimeout(fallbackTimer);
    }
  }, [currentStage]);

  // Fallback progression: slowly advance through stages if no real updates
  useEffect(() => {
    if (fallbackActive) {
      const progressionTimer = setInterval(() => {
        setStageIdx(prev => {
          const nextIdx = Math.min(prev + 1, Object.values(STAGE_MAP).length - 2); // Don't go to citations
          const nextStage = Object.values(STAGE_MAP)[nextIdx];
          if (nextStage) {
            setDisplayMessage(`${nextStage.label} (estimated)`);
          }
          return nextIdx;
        });
      }, 30000); // Advance every 30 seconds

      return () => clearInterval(progressionTimer);
    }
  }, [fallbackActive]);

  const stages = Object.values(STAGE_MAP);

  return (
    <div className="flex flex-col items-center w-full">
      {/* <div className="text-xs text-text-tertiary dark:text-dark-textSecondary mb-1 px-1 w-[80%] text-left"> */}
        {/* Shipment CoCaptain is thinking... */}
      {/* </div> */}
      <div className="w-full flex flex-col items-center">
        {/* Progress bar/stepper */}
        <div className="flex items-center justify-center mb-2">
          {stages.map((stage, idx) => (
            <React.Fragment key={stage.label}>
              <div
                className={`h-2 w-2 rounded-full transition-colors duration-300 ${
                  idx < stageIdx
                    ? 'bg-primary'
                    : idx === stageIdx
                    ? 'bg-primary animate-pulse'
                    : 'bg-gray-300'
                }`}
              />
              {idx < stages.length - 1 && <div className={`w-6 h-1 mx-1 ${
                idx < stageIdx ? 'bg-primary' : 'bg-gray-200'
                }`}
              />}
            </React.Fragment>
          ))}
        </div>
        
        {/* Progress percentage bar if available */}
        {/* {progress !== undefined && progress > 0 && (
          <div className="w-[80%] mb-2">
            <div className="flex justify-between text-xs text-text-tertiary dark:text-dark-textSecondary mb-1">
              <span>Progress</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1">
              <div 
                className="bg-primary h-1 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
              />
            </div>
          </div>
        )} */}
        
        {/* Display message - hidden when MessageThinking is shown */}
        {!hideText && (
          <div className="text-sm text-text-secondary dark:text-dark-textSecondary mb-2">
            {displayMessage}
          </div>
        )}
        
        {/* "Shipment CoCaptain is thinking" - hidden when MessageThinking is shown */}
        {!hideText && (
          <div className="w-full flex justify-center">
            <div className="w-full sm:w-[95%] md:w-[85%] lg:w-[80%]">
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="h-2 w-2 bg-primary rounded-full animate-pulse"
                      style={{ animationDelay: `${i * 0.2}s` }}
                    ></div>
                  ))}
                </div>
                <span className="text-text-secondary dark:text-dark-textSecondary text-sm">Shipment CoCaptain is thinking...</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThinkingIndicator;
